import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rydey_admin_token";
const EXP_KEY = "rydey_admin_exp";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(true);

  const isExpired = useCallback(() => {
    const exp = Number(localStorage.getItem(EXP_KEY) || 0);
    return !exp || Date.now() >= exp;
  }, []);

  useEffect(() => {
    const verify = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t || isExpired()) {
        clearAuth();
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setToken(t);
      } catch {
        clearAuth();
      } finally {
        setChecking(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (password) => {
    const { data } = await axios.post(`${API}/admin/login`, { password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(EXP_KEY, String(new Date(data.expires_at).getTime()));
    setToken(data.token);
    return data;
  };

  const clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXP_KEY);
    setToken(null);
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API}/admin/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      /* ignore */
    }
    clearAuth();
  };

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  return (
    <AdminAuthContext.Provider
      value={{ token, checking, isAuthenticated: !!token, login, logout, authHeader }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
